import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Image, Text, Flex } from 'atoms';
import { saveDenominations, getDenominations, dashboardDetails } from 'store/invoiceSlice';
import closeIcon from 'assets/close-icon.svg';
import { useQueryParams } from 'hooks';
import { Rupee } from 'components';
import { getTotalMoney, onAction } from './DenominationFunctions';
import * as Styled from './Denomination.style';

export default function Denomination({ onClose, detailView }) {
	const dispatch = useDispatch();
	const {
		denomination = {},
		dashboard,
		invoices = [],
		receivedInvoiceList = [],
	} = useSelector((state) => state.invoice);
	const { invoiceDate } = useSelector((state) => state.app);
	const [money, setMoney] = useState([]);
	const [totalAmount, setTotalAmount] = useState(0);
	const { salesmanId = undefined } = useQueryParams();

	useEffect(() => {
		dispatch(dashboardDetails(salesmanId));
		dispatch(getDenominations(salesmanId));
	}, [salesmanId, dispatch]);

	useEffect(() => {
		setMoney([
			{
				money_id: 1,
				key: 'two_thousand',
				label: '2000',
				amount: 2000,
				count: denomination?.two_thousand,
			},
			{
				money_id: 2,
				key: 'five_hundred',
				label: '500',
				amount: 500,
				count: denomination?.five_hundred,
			},
			{
				money_id: 3,
				key: 'two_hundred',
				label: '200',
				amount: 200,
				count: denomination?.two_hundred,
			},
			{
				money_id: 4,
				key: 'hundred',
				label: '100',
				amount: 100,
				count: denomination?.hundred,
			},
			{
				money_id: 5,
				key: 'fifty',
				label: '50',
				amount: 50,
				count: denomination?.fifty,
			},
			{
				money_id: 6,
				key: 'twenty',
				label: '20',
				amount: 20,
				count: denomination?.twenty,
			},
			{
				money_id: 7,
				key: 'ten',
				label: '10',
				amount: 10,
				count: denomination?.ten,
			},
			{
				money_id: 8,
				key: 'five',
				label: '5',
				amount: 5,
				count: denomination?.five,
			},
			{
				money_id: 9,
				key: 'coins',
				label: 'Coins',
				amount: 1,
				count: denomination?.coins,
			},
		]);
	}, [denomination]);

	useEffect(() => {
		const totalMoney = getTotalMoney(money);

		setTotalAmount(totalMoney);
	}, [money]);

	function plus(data) {
		setMoney(onAction(money, data, 'plus', ''));
	}

	function minus(data) {
		if (data?.count && data?.count > -1) {
			setMoney(onAction(money, data, 'minus', ''));
		}
	}

	function updateCount(data, value) {
		setMoney(onAction(money, data, 'count_update', value));
	}

	function handleSave() {
		const object = money.reduce(
			(obj, item) => Object.assign(obj, { [item.key]: item.count }),
			{}
		);

		object.salesman_id = salesmanId;
		object.invoice_date = invoiceDate;
		dispatch(saveDenominations(object)).then(() => {
			onClose();
			detailView();
		});
	}

	function totalInvoiceCopy() {
		if (invoices.length && receivedInvoiceList.length) {
			return invoices.length + receivedInvoiceList.length;
		}
		if (receivedInvoiceList.length) {
			return receivedInvoiceList.length;
		}

		return invoices.length;
	}

	return (
		<Styled.Container>
			<Styled.Header position="relative">
				<Flex justifyContent="space-between" alignItems="flex-start">
					<Box>
						<Text fontSize="12px">Denomination</Text>
					</Box>
					<Box textAlign="right">
						<Text fontSize="12px">{totalInvoiceCopy()}</Text>
						<Text fontSize="8px" fontWeight="300" pt="5px">
							Total Invoice Copy
						</Text>
					</Box>
					<Box textAlign="right">
						<Text fontSize="12px" fontWeight="600" color="#10a744">
							<Rupee>{dashboard?.cash}</Rupee>
						</Text>
						<Text fontSize="8px" fontWeight="300" pt="5px">
							Cash Collected
						</Text>
					</Box>
				</Flex>
				<Box cursor="pointer" onClick={onClose} position="absolute" top="-35px" right="0px">
					<Image src={closeIcon} alt="Close-Icon" height={33} width={33} />
				</Box>
			</Styled.Header>
			<Styled.Body>
				<Flex justifyContent="space-between" py="1rem">
					<Text fontSize="10px" fontWeight="300" pl="2rem">
						Value
					</Text>
					<Text fontSize="10px" fontWeight="300">
						Count
					</Text>
					<Text fontSize="10px" fontWeight="300" pr="2.5rem">
						Total
					</Text>
				</Flex>
				<Box
					border="1px solid #303030"
					borderRadius="4px"
					padding="1rem"
					maxHeight="300px"
					overflow="auto"
				>
					{money.map((item) => (
						<Flex
							justifyContent="space-between"
							alignItems="center"
							key={item.money_id}
						>
							<Text minWidth="40px" textAlign="left" fontSize="12px" color="#000">
								{item?.label}
							</Text>
							<Flex
								justifyContent="space-between"
								alignItems="center"
								px="1rem"
								borderLeft="1px solid #303030"
								borderRight="1px solid #303030"
								py=".5rem"
							>
								<Styled.Minus onClick={() => minus(item)} />
								<Styled.Input
									value={item?.count || 0}
									type="number"
									min="0"
									onChange={(e) => updateCount(item, Number(e.target.value))}
								/>
								<Styled.Plus onClick={() => plus(item)} />
							</Flex>
							<Box
								borderBottom="1px solid #303030"
								height="32px"
								width="65px"
								textAlign="center"
								pt="5px"
							>
								<Text textAlign="center" fontSize="12px" color="#000">
									{(item?.amount || 0) * (item?.count || 0)}
								</Text>
							</Box>
						</Flex>
					))}
				</Box>
				<Text fontSize="12px" fontWeight="600" textAlign="right" pt="5px" pr="3rem">
					Total: <Rupee>{totalAmount}</Rupee>
				</Text>
				<Box textAlign="center">
					<Styled.FinishBtn
						type="primary"
						disabled={dashboard?.cash !== totalAmount}
						onClick={handleSave}
					>
						Finish
					</Styled.FinishBtn>
				</Box>
			</Styled.Body>
		</Styled.Container>
	);
}

Denomination.propTypes = {
	onClose: PropTypes.func.isRequired,
	detailView: PropTypes.func,
};
